import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { Notification, NotificationType } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}

const TYPE_CONFIG: Record<NotificationType, { icon: React.ElementType; color: string }> = {
  project_assigned: { icon: AssignmentIndOutlinedIcon, color: colors.softGreen },
  member_auto_added: { icon: AssignmentIndOutlinedIcon, color: colors.yellow },
  timesheet_approved: { icon: ThumbUpOutlinedIcon, color: colors.softGreen },
  timesheet_rejected: { icon: ThumbDownOutlinedIcon, color: '#FFCCCC' },
  time_reminder: { icon: NotificationsOutlinedIcon, color: colors.peach },
  timesheet_reminder: { icon: NotificationsOutlinedIcon, color: colors.peach },
  budget_alert: { icon: NotificationsOutlinedIcon, color: colors.yellow },
  milestone_due: { icon: NotificationsOutlinedIcon, color: colors.warmBeige },
  general: { icon: NotificationsOutlinedIcon, color: colors.lightGray },
};

export default function NotificationCard({ notification, onMarkAsRead }: NotificationCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = TYPE_CONFIG[notification.type] ?? TYPE_CONFIG.general;
  const Icon = config.icon;

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    if (notification.projectId) {
      navigate(`/projects/${notification.projectId}`);
    }
  };

  const timeAgo = notification.createdAt?.toDate
    ? formatTimeAgo(notification.createdAt.toDate(), t)
    : '';

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: notification.projectId ? 'pointer' : 'default',
        opacity: notification.read ? 0.7 : 1,
        bgcolor: notification.read ? colors.white : '#FAFAFA',
        borderLeft: `4px solid ${config.color}`,
        '&:hover': notification.projectId ? { bgcolor: colors.lightGray } : {},
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Icon sx={{ color: colors.textMuted, fontSize: 20, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: notification.read ? 400 : 600 }}>
              {notification.title}
            </Typography>
            {!notification.read && (
              <Chip
                label={t('notifications.new')}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: colors.red,
                  color: colors.white,
                }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {notification.body}
          </Typography>
          {timeAgo && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {timeAgo}
            </Typography>
          )}
        </Box>
        {!notification.read && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead(notification.id);
            }}
            aria-label={t('notifications.markRead')}
            sx={{ color: colors.textMuted }}
          >
            <CheckCircleOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </CardContent>
    </Card>
  );
}

function formatTimeAgo(date: Date, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return t('notifications.time.justNow');
  if (minutes < 60) return t('notifications.time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notifications.time.daysAgo', { count: days });
}
